import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiUsers, FiClock, FiRefreshCw, FiCheck, FiX,
  FiShield, FiZap, FiEye, FiSettings, FiDatabase, FiEdit3
} from 'react-icons/fi';
import { HiOutlineWrenchScrewdriver } from 'react-icons/hi2';
import api from '../../utils/api';
import { showToast } from '../../utils/toast';
import useConfirm from '../../hooks/useConfirm';
import {
  formatMaintenanceDateTimeDisplay,
  formatMaintenanceDateTimeForInput,
} from '../../utils/date';
import './Admin.scss';

const TIERS = [
  {
    id: 'free',
    label: 'Free',
    desc: 'Trial & free plan users',
    emoji: '🆓',
    color: '#6366f1',
    gradient: 'linear-gradient(135deg, #6366f1, #818cf8)',
  },
  {
    id: 'silver',
    label: 'Silver',
    desc: 'Silver plan subscribers',
    emoji: '🥈',
    color: '#9ca3af',
    gradient: 'linear-gradient(135deg, #9ca3af, #d1d5db)',
  },
  {
    id: 'gold',
    label: 'Gold',
    desc: 'Gold plan subscribers',
    emoji: '🥇',
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
  },
];

const Maintenance = () => {
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [settings, setSettings] = useState({
    isEnabled: false,
    affectedTiers: ['all'],
    title: 'System Maintenance',
    message: "We're currently performing maintenance. Please check back soon.",
    estimatedEndTime: '',
    allowAdminAccess: true,
    enabledBy: null,
    enabledAt: null
  });
  const [affectedCount, setAffectedCount] = useState(0);
  const [breakdown, setBreakdown] = useState({ free: 0, silver: 0, gold: 0, total: 0 });

  const tierPercentages = useMemo(() => {
    const total = breakdown.total || 1;
    return {
      free: Math.round((breakdown.free / total) * 100),
      silver: Math.round((breakdown.silver / total) * 100),
      gold: Math.round((breakdown.gold / total) * 100),
    };
  }, [breakdown]);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, countRes] = await Promise.all([
        api.get('/maintenance/settings'),
        api.get('/maintenance/affected-count')
      ]);

      const data = settingsRes.data;
      setSettings({
        isEnabled: data.isEnabled || false,
        affectedTiers: data.affectedTiers || ['all'],
        title: data.title || 'System Maintenance',
        message: data.message || "We're currently performing maintenance. Please check back soon.",
        estimatedEndTime: formatMaintenanceDateTimeForInput(data.estimatedEndTime),
        allowAdminAccess: data.allowAdminAccess !== false,
        enabledBy: data.enabledBy,
        enabledAt: data.enabledAt
      });

      setAffectedCount(countRes.data.affectedCount || 0);
      if (countRes.data.breakdown) setBreakdown(countRes.data.breakdown);
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to fetch maintenance settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    const getAffectedCount = async () => {
      try {
        const tiersParam = settings.affectedTiers.join(',');
        const res = await api.get(`/maintenance/affected-count?tiers=${encodeURIComponent(tiersParam)}`);
        setAffectedCount(res.data.affectedCount || 0);
        if (res.data.breakdown) setBreakdown(res.data.breakdown);
      } catch (err) {
        console.error('Failed to get affected count');
      }
    };
    if (!loading) getAffectedCount();
  }, [settings.affectedTiers, loading]);

  const handleToggleMaintenance = async () => {
    if (!settings.isEnabled) {
      const confirmed = await confirm(
        `Enable maintenance mode? This will affect ${affectedCount} user(s).`,
        {
          title: 'Enable Maintenance Mode',
          confirmText: 'Enable',
          showWarning: true,
        }
      );
      if (!confirmed) return;
    }
    setToggling(true);
    try {
      await api.post('/maintenance/toggle');
      showToast('success', settings.isEnabled ? 'Maintenance mode disabled' : 'Maintenance mode enabled');
      fetchSettings();
    } catch (err) {
      showToast('error', 'Failed to toggle maintenance mode');
    } finally {
      setToggling(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/maintenance/settings', {
        ...settings,
        estimatedEndTime: settings.estimatedEndTime || null
      });
      showToast('success', 'Maintenance settings saved');
      fetchSettings();
    } catch (err) {
      showToast('error', 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTierChange = (tier) => {
    setSettings(prev => {
      let newTiers = [...prev.affectedTiers];
      if (tier === 'all') {
        newTiers = newTiers.includes('all') ? [] : ['all'];
      } else {
        newTiers = newTiers.filter(t => t !== 'all');
        if (newTiers.includes(tier)) {
          newTiers = newTiers.filter(t => t !== tier);
        } else {
          newTiers.push(tier);
        }
        if (newTiers.length === 0) newTiers = ['all'];
      }
      return { ...prev, affectedTiers: newTiers };
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="admin-maintenance">
        <div className="maint-skeleton">
          <div className="maint-skeleton__hero" />
          <div className="maint-skeleton__stats">
            <div className="maint-skeleton__stat-card" />
            <div className="maint-skeleton__stat-card" />
            <div className="maint-skeleton__stat-card" />
          </div>
          <div className="maint-skeleton__row">
            <div className="maint-skeleton__col">
              <div className="maint-skeleton__card" />
              <div className="maint-skeleton__card--sm" />
            </div>
            <div className="maint-skeleton__col">
              <div className="maint-skeleton__card" />
              <div className="maint-skeleton__card" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isActive = settings.isEnabled;

  return (
    <div className="admin-maintenance">

      {/* ── Hero Status Banner ─────────────────────── */}
      <div className={`maint-hero ${isActive ? 'maint-hero--active' : 'maint-hero--online'}`}>
        <div className="maint-hero__bg-orb maint-hero__bg-orb--1" />
        <div className="maint-hero__bg-orb maint-hero__bg-orb--2" />
        <div className="maint-hero__bg-orb maint-hero__bg-orb--3" />

        <div className="maint-hero__left">
          <div className="maint-hero__badge">
            <span className={`maint-hero__dot ${isActive ? 'maint-hero__dot--active' : 'maint-hero__dot--idle'}`} />
            {isActive ? 'Maintenance Active' : 'All Systems Operational'}
          </div>
          <h2 className="maint-hero__title">
            {isActive ? 'Maintenance Mode is ON' : 'All Systems Operational'}
          </h2>
          <p className="maint-hero__sub">
            {isActive
              ? `Enabled by ${settings.enabledBy?.name || 'Admin'} · ${formatDate(settings.enabledAt)}`
              : 'No restrictions active · Users have full access'}
          </p>
        </div>

        <div className="maint-hero__right">
          <button
            className={`maint-toggle-btn ${isActive ? 'maint-toggle-btn--off' : 'maint-toggle-btn--on'}`}
            onClick={handleToggleMaintenance}
            disabled={toggling || saving}
          >
            {toggling
              ? <><FiRefreshCw className="spin" /> Working…</>
              : isActive
                ? <><FiX /> Disable</>
                : <><FiZap /> Enable</>}
          </button>
          <button className="maint-hero__refresh" onClick={fetchSettings} disabled={loading}>
            <FiRefreshCw className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── Quick Stats Row ──────────────────────── */}
      <div className="maint-stats">
        <div className="maint-stat-card">
          <div className="maint-stat-card__icon maint-stat-card__icon--users">
            <FiDatabase />
          </div>
          <div className="maint-stat-card__content">
            <span className="maint-stat-card__value">{breakdown.total ?? 0}</span>
            <span className="maint-stat-card__label">Total Users</span>
          </div>
          <div className="maint-stat-card__meta">Free + Silver + Gold</div>
        </div>
        <div className={`maint-stat-card ${affectedCount > 0 ? 'maint-stat-card--highlight' : ''}`}>
          <div className="maint-stat-card__icon maint-stat-card__icon--affected">
            <FiUsers />
          </div>
          <div className="maint-stat-card__content">
            <span className="maint-stat-card__value">{affectedCount}</span>
            <span className="maint-stat-card__label">Affected Users</span>
          </div>
          <div className="maint-stat-card__meta">Excluding admins</div>
        </div>
        <div className="maint-stat-card">
          <div className="maint-stat-card__icon maint-stat-card__icon--shield">
            <FiShield />
          </div>
          <div className="maint-stat-card__content">
            <span className="maint-stat-card__value">{settings.allowAdminAccess ? 'On' : 'Off'}</span>
            <span className="maint-stat-card__label">Admin Bypass</span>
          </div>
          <div className="maint-stat-card__meta">{settings.allowAdminAccess ? 'Admins see app' : 'Admins blocked too'}</div>
        </div>
        {isActive && settings.estimatedEndTime && (
          <div className="maint-stat-card">
            <div className="maint-stat-card__icon maint-stat-card__icon--time">
              <FiClock />
            </div>
            <div className="maint-stat-card__content">
              <span className="maint-stat-card__value">{formatMaintenanceDateTimeDisplay(settings.estimatedEndTime)}</span>
              <span className="maint-stat-card__label">Ends At</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Main Content ─────────────────────────── */}
      <form onSubmit={handleSaveSettings} className="maint-body">

        {/* Left column */}
        <div className="maint-col maint-col--left">

          {/* Affected Tiers */}
          <div className="maint-card">
            <div className="maint-card__head">
              <span className="maint-card__head-icon"><FiUsers /></span>
              <div>
                <h3>Affected Tiers</h3>
                <p>Select who sees the maintenance screen</p>
              </div>
            </div>
            <div className="maint-card__body">
              {/* Tier distribution bar */}
              <div className="maint-tier-bar">
                {TIERS.map(t => (
                  <div
                    key={t.id}
                    className="maint-tier-bar__segment"
                    style={{
                      width: `${tierPercentages[t.id] || 0}%`,
                      background: t.gradient,
                    }}
                    title={`${t.label}: ${breakdown[t.id] || 0}`}
                  />
                ))}
                {(breakdown.total || 0) === 0 && (
                  <div className="maint-tier-bar__segment maint-tier-bar__segment--empty" />
                )}
              </div>
              <div className="maint-tier-bar__legend">
                {TIERS.map(t => (
                  <span key={t.id} className="maint-tier-bar__legend-item">
                    <span className="maint-tier-bar__dot" style={{ background: t.color }} />
                    {t.label} ({breakdown[t.id] || 0})
                  </span>
                ))}
              </div>

              {/* All Users toggle */}
              <label className={`maint-tier-option ${settings.affectedTiers.includes('all') ? 'maint-tier-option--selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={settings.affectedTiers.includes('all')}
                  onChange={() => handleTierChange('all')}
                />
                <div className="maint-tier-option__check">
                  {settings.affectedTiers.includes('all') && <FiCheck />}
                </div>
                <div className="maint-tier-option__content">
                  <div className="maint-tier-option__header">
                    <span className="maint-tier-option__emoji">👥</span>
                    <strong>All Users</strong>
                    <span className="maint-tier-option__count">{breakdown.total ?? 0}</span>
                  </div>
                  <span className="maint-tier-option__desc">Free + Silver + Gold · Admins excluded</span>
                </div>
              </label>

              <div className="maint-tier-divider"><span>or pick tiers</span></div>

              {/* Individual tier cards */}
              <div className="maint-tier-grid">
                {TIERS.map(t => {
                  const isSelected = settings.affectedTiers.includes(t.id);
                  const isDisabled = settings.affectedTiers.includes('all');
                  const count = breakdown[t.id] || 0;
                  const pct = tierPercentages[t.id] || 0;
                  return (
                    <label
                      key={t.id}
                      className={`maint-tier-option maint-tier-option--tier ${isSelected ? 'maint-tier-option--selected' : ''} ${isDisabled ? 'maint-tier-option--disabled' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleTierChange(t.id)}
                        disabled={isDisabled}
                      />
                      <div className="maint-tier-option__check" style={isSelected ? { background: t.color, borderColor: t.color } : {}}>
                        {isSelected && <FiCheck />}
                      </div>
                      <div className="maint-tier-option__content">
                        <div className="maint-tier-option__header">
                          <span className="maint-tier-option__emoji">{t.emoji}</span>
                          <strong>{t.label}</strong>
                          <span className="maint-tier-option__count">{count}</span>
                        </div>
                        <div className="maint-tier-option__progress-track">
                          <div
                            className="maint-tier-option__progress-fill"
                            style={{ width: `${pct}%`, background: t.gradient }}
                          />
                        </div>
                        <span className="maint-tier-option__desc">{t.desc} · {pct}%</span>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="maint-card">
            <div className="maint-card__head">
              <span className="maint-card__head-icon"><FiSettings /></span>
              <div>
                <h3>Options</h3>
                <p>Fine-tune maintenance behaviour</p>
              </div>
            </div>
            <div className="maint-card__body">
              <label className="maint-toggle-row">
                <input
                  type="checkbox"
                  checked={settings.allowAdminAccess}
                  onChange={(e) => setSettings(prev => ({ ...prev, allowAdminAccess: e.target.checked }))}
                />
                <span className="maint-toggle-row__switch" />
                <span className="maint-toggle-row__info">
                  <strong>Admin Bypass</strong>
                  <small>Admins can still access the system during maintenance</small>
                </span>
              </label>
            </div>
          </div>

        </div>

        {/* Right column */}
        <div className="maint-col maint-col--right">

          {/* Message Settings */}
          <div className="maint-card">
            <div className="maint-card__head">
              <span className="maint-card__head-icon"><FiEdit3 /></span>
              <div>
                <h3>Maintenance Message</h3>
                <p>What users will see on the maintenance screen</p>
              </div>
            </div>
            <div className="maint-card__body">
              <div className="maint-form-group">
                <label htmlFor="maint-title">Page Title</label>
                <input
                  type="text"
                  id="maint-title"
                  value={settings.title}
                  onChange={(e) => setSettings(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="System Maintenance"
                  maxLength={100}
                />
              </div>

              <div className="maint-form-group">
                <label htmlFor="maint-message">
                  Message
                  <span className="maint-char-count">{settings.message.length}/500</span>
                </label>
                <textarea
                  id="maint-message"
                  value={settings.message}
                  onChange={(e) => setSettings(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Enter maintenance message…"
                  rows={4}
                  maxLength={500}
                />
              </div>

              <div className="maint-form-group">
                <label htmlFor="maint-end">
                  <FiClock style={{ marginRight: '0.375rem', verticalAlign: 'middle' }} />
                  Estimated End Time
                  <span className="maint-optional">optional</span>
                </label>
                <input
                  type="datetime-local"
                  id="maint-end"
                  value={settings.estimatedEndTime}
                  onChange={(e) => setSettings(prev => ({ ...prev, estimatedEndTime: e.target.value }))}
                />
                <small>Leave empty if unknown</small>
              </div>
            </div>
          </div>

          {/* Live Preview */}
          <div className="maint-card maint-card--preview">
            <div className="maint-card__head">
              <span className="maint-card__head-icon"><FiEye /></span>
              <div>
                <h3>Live Preview</h3>
                <p>How the maintenance screen looks to users</p>
              </div>
            </div>
            <div className="maint-card__body">
              <div className="maint-preview">
                <div className="maint-preview__browser-bar">
                  <span className="maint-preview__dots">
                    <span /><span /><span />
                  </span>
                  <span className="maint-preview__url">miventsite.com</span>
                </div>
                <div className="maint-preview__screen">
                  <div className="maint-preview__glow" />
                  <div className="maint-preview__gear-wrap">
                    <div className="maint-preview__gear">
                      <HiOutlineWrenchScrewdriver />
                    </div>
                    <div className="maint-preview__rings">
                      <span /><span /><span />
                    </div>
                  </div>
                  <h4 className="maint-preview__title">
                    {settings.title || 'System Maintenance'}
                  </h4>
                  <p className="maint-preview__msg">
                    {settings.message || 'No message set.'}
                  </p>
                  {settings.estimatedEndTime && (
                    <div className="maint-preview__time">
                      <FiClock />
                      Back online: {formatMaintenanceDateTimeDisplay(settings.estimatedEndTime)}
                    </div>
                  )}
                  <div className="maint-preview__progress">
                    <span />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="maint-actions">
            <button type="submit" className="maint-save-btn" disabled={saving || toggling}>
              {saving
                ? <><FiRefreshCw className="spin" /> Saving…</>
                : <><FiCheck /> Save Settings</>}
            </button>
          </div>

        </div>
      </form>
    </div>
  );
};

export default Maintenance;
